import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import _ from 'lodash';

import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import ReloadViewModule from '../../../services/ReloadViewModule';
import SaveCustomerOrderDevices from '../../../services/SaveCustomerOrderDevices';
import UpdateOrderLineItems from '../../../services/UpdateOrderLineItems';
import DefaultDeviceSectionView from '../../../views/DefaultDeviceSectionView';

import tmplDefaultFormView from './DefaultFormView.hbs';

const DefaultFormView = View.extend({
  template: tmplDefaultFormView,

  ui: {
    descriptionSection: '.description-section',
    securityFeaturesSection: '.security-features-section',
    niprMfdSection: '.nipr-mfd-section',
    siprMfdSection: '.sipr-mfd-section',
    additionalItemsSection: '.additional-items-section',
    devicesAlert: '.no-devices-error'
  },

  regions: {
    descriptionSection: '@ui.descriptionSection',
    securityFeaturesSection: '@ui.securityFeaturesSection',
    niprMfdSection: '@ui.niprMfdSection',
    siprMfdSection: '@ui.siprMfdSection',
    additionalItemsSection: '@ui.additionalItemsSection'
  },

  className: 'default-form-view',

  onRender() {
    this.defaultDeviceSectionView = new DefaultDeviceSectionView();
    const orderType = this.model.get('orderDetails').orderTypeId;
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;

    const { NIPR_MFD, SIPR_MFD, AdditionalItems } = _.groupBy(this.model.get('lineItems'), 'panel');
    this.showChildView(
      'descriptionSection',
      new DefaultDeviceSectionView({
        model: new Backbone.Model({
          title: 'Device Information',
          type: 'description',
          contract: orderType,
          id: 'Conus-Description',
          isAdminEdit
        })
      })
    );

    const isEuropeMiddleEastForm = ['23', '24', '25', '26'].includes(orderType);
    if (isEuropeMiddleEastForm) {
      this.showChildView(
        'securityFeaturesSection',
        new DefaultDeviceSectionView({
          model: new Backbone.Model({
            title: 'Security Features',
            type: 'securityFeatures',
            contract: orderType,
            id: 'Conus-Security-Features',
            isAdminEdit
          })
        })
      );
    }

    this.showChildView(
      'niprMfdSection',
      new DefaultDeviceSectionView({
        model: new Backbone.Model({
          lineItems: NIPR_MFD,
          title: 'NIPR Desktop Multifunctional Devices (MFDs)',
          id: 'niprMfd',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'siprMfdSection',
      new DefaultDeviceSectionView({
        model: new Backbone.Model({
          lineItems: SIPR_MFD,
          title: 'SIPR Desktop Multifunctional Devices (MFDs)',
          id: 'siprMfd',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );

    if (this.model.get('orderDetails').isAdminEdit) {
      this.showChildView(
        'additionalItemsSection',
        new DefaultDeviceSectionView({
          model: new Backbone.Model({
            lineItems: AdditionalItems,
            title: 'Additional Items',
            id: 'additionalItems',
            orderType,
            type: 'additionalItems',
            isAdminEdit
          })
        })
      );
    }
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  },

  showNoChangedDevicesError() {
    showErrorMessage({
      errorCode: 'noChangedDevicesError',
      errorTitle: 'No Devices Updated Error',
      errorMessage: 'No devices were updated. Please update devices in order to save changes.'
    });
  },

  async validate() {
    const [niprMfdColl, siprMfdColl] = await this.getDeviceData();
    let isValid = true;
    if (!niprMfdColl.totalQuantity && !siprMfdColl.totalQuantity) {
      isValid = false;
      this.ui.devicesAlert.show();
    }
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');
    return isValid;
  },

  async getDeviceData() {
    return await Promise.all([
      this.getChildView('niprMfdSection').getDeviceData(),
      this.getChildView('siprMfdSection').getDeviceData()
    ]);
  },

  getLineItemData(lineItemData) {
    const lineItems = this.model.get('lineItems');
    const changedItems = [];

    _.each(lineItems, (item) => {
      const changedPrice = lineItemData[`${item.id}-price`];
      const changedDesc = lineItemData[`${item.id}-description`];
      const changedConfig = lineItemData[`${item.id}-configurationAndPricing`];
      const changedDlaCost = lineItemData[`${item.id}-dlaCost`];

      const isPriceChanged = item.price !== changedPrice && item.price;
      const isDescChanged = item.description !== changedDesc && item.description;
      const isConfigChanged = item.configurationAndPricing !== changedConfig && item.configurationAndPricing;
      const isDlaCostChanged = item.dlaCost !== changedDlaCost && !!changedDlaCost;

      const changedItem = {};
      if (isPriceChanged) changedItem.price = parseFloat(changedPrice).toFixed(2);
      if (isDescChanged) changedItem.description = changedDesc;
      if (isConfigChanged) changedItem.configurationAndPricing = changedConfig;
      if (isDlaCostChanged) changedItem.dlaCost = changedDlaCost;
      if (!_.isEmpty(changedItem)) {
        changedItem.id = item.id;
        changedItems.push(changedItem);
      }
    });

    return changedItems;
  },

  async onSave(lineItemData) {
    this.ui.devicesAlert.hide();
    this.trigger('no:errors');
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;
    let formData;

    try {
      if (isAdminEdit && lineItemData) {
        formData = await this.saveAdminEdit(lineItemData);
        if (formData.changedItems.length > 0) {
          await UpdateOrderLineItems(formData);
          this.showSaveSuccess();
        } else {
          this.showNoChangedDevicesError();
        }
      } else {
        formData = await this.saveOrder();
        await Promise.all([SaveCustomerOrderDevices(formData), ReloadViewModule.reload()]);
        this.showSaveSuccess();
      }
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  },

  async saveOrder() {
    const [niprMfdColl, siprMfdColl] = await this.getDeviceData();

    const devicesFormData = niprMfdColl.changedItems.concat(siprMfdColl.changedItems);

    const formData = {
      devicesFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId,
      itPocExists: true
    };

    return formData;
  },

  saveAdminEdit(lineItemData) {
    const changedItems = this.getLineItemData(lineItemData);
    const formData = {
      orderFormTypeID: this.model.get('orderDetails').orderTypeId,
      changedItems
    };

    return formData;
  }
});

export default DefaultFormView;
